/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import * as Sentry from "@sentry/react";

/**
 * N-18 (Session 376): both wrappers below used to report through
 * `window.Sentry?.captureException(error)`, and nothing in this application
 * ever puts Sentry on `window`. It is installed as a package, started in
 * main.tsx with `Sentry.init`, and imported as a module by every other file
 * that reports — the chat panel, the API client, the error boundary. Only the
 * script-tag build attaches itself to the global object, and that is not what
 * is used here. So the optional chain was always undefined and all six
 * reporting paths in this file were silently dead: the one class of error these
 * wrappers exist to make visible was the one class nobody ever saw.
 *
 * Importing the module the way the rest of the code base does makes six
 * existing paths work rather than adding a new one. It is done now because P-1
 * is the first change to depend on this wrapper for something a customer can
 * notice — a chat conversation that is or is not cleared.
 */

/**
 * Safe localStorage wrapper that handles private browsing mode
 * and blocked storage gracefully without throwing exceptions.
 */
export const safeLocalStorage = {
  getItem(key: string): string | null {
    try {
      /* c8 ignore next -- typeof window is always defined in browser/jsdom environments */
      if (typeof window !== "undefined" && window.localStorage) {
        return localStorage.getItem(key);
      }
    } catch (error) {
      // SecurityError / DOMException is expected in private browsing — not reported.
      // Any other error type is unexpected and should be visible in Sentry.
      if (!(error instanceof DOMException)) {
        Sentry.captureException(error);
      }
    }
    return null;
  },

  /**
   * N-4 (Session 447): returns whether the value was actually written.
   *
   * It used to return void, so every caller was told a refusal and a success
   * apart from each other in exactly one way: not at all. That is tolerable
   * for a remembered language or currency and is not tolerable for the
   * session key - a sign-in whose key could not be stored has not really
   * succeeded, and the page can only say so if it is told.
   */
  setItem(key: string, value: string): boolean {
    try {
      /* c8 ignore next -- typeof window is always defined in browser/jsdom environments */
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.setItem(key, value);
        return true;
      }
    } catch (error) {
      if (!(error instanceof DOMException)) {
        Sentry.captureException(error);
      }
    }
    return false;
  },

  removeItem(key: string): void {
    try {
      /* c8 ignore next -- typeof window is always defined in browser/jsdom environments */
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.removeItem(key);
      }
    } catch (error) {
      if (!(error instanceof DOMException)) {
        Sentry.captureException(error);
      }
    }
  },
};

/**
 * Stored currency data shape, matching the API response and CurrencyConverter output.
 */
export interface StoredCurrencyData {
  currency_code: string;
  currency_symbol: string;
  exchange_rate: number;
}

/**
 * Get currency data from localStorage safely.
 * Returns null if not set, unavailable, or JSON is malformed.
 */
export const getStoredCurrencyData = (): StoredCurrencyData | null => {
  const raw = safeLocalStorage.getItem("currencyData");
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "currency_code" in parsed &&
      "currency_symbol" in parsed &&
      "exchange_rate" in parsed &&
      typeof (parsed as StoredCurrencyData).currency_code === "string" &&
      typeof (parsed as StoredCurrencyData).currency_symbol === "string" &&
      typeof (parsed as StoredCurrencyData).exchange_rate === "number"
    ) {
      return parsed as StoredCurrencyData;
    }
  } catch {
    // Malformed JSON — treat as missing
  }
  return null;
};

/**
 * Get language from localStorage safely
 * @returns Language code or null if not set/unavailable
 */
export const safeGetLang = (): string | null => {
  return safeLocalStorage.getItem("lang");
};

/**
 * Set language in localStorage safely
 * @param lang - Language code to store
 */
export const safeSetLang = (lang: string): void => {
  safeLocalStorage.setItem("lang", lang);
};

/**
 * Get language with fallback to English
 * @returns Language code, defaults to "en"
 */
export const getLangWithFallback = (): string => {
  return safeLocalStorage.getItem("lang") ?? "en";
};

export default safeLocalStorage;

/**
 * Safe sessionStorage wrapper that mirrors safeLocalStorage.
 * sessionStorage is blocked in private browsing mode on some browsers and can
 * throw a SecurityError / DOMException. All three methods silently no-op on
 * DOMException; any other unexpected error is forwarded to Sentry.
 */
export const safeSessionStorage = {
  getItem(key: string): string | null {
    try {
      /* c8 ignore next -- typeof window is always defined in browser/jsdom environments */
      if (typeof window !== "undefined" && window.sessionStorage) {
        return sessionStorage.getItem(key);
      }
    } catch (error) {
      if (!(error instanceof DOMException)) {
        Sentry.captureException(error);
      }
    }
    return null;
  },

  /** Mirrors safeLocalStorage.setItem, including its success value (N-4). */
  setItem(key: string, value: string): boolean {
    try {
      /* c8 ignore next -- typeof window is always defined in browser/jsdom environments */
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.setItem(key, value);
        return true;
      }
    } catch (error) {
      if (!(error instanceof DOMException)) {
        Sentry.captureException(error);
      }
    }
    return false;
  },

  removeItem(key: string): void {
    try {
      /* c8 ignore next -- typeof window is always defined in browser/jsdom environments */
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.removeItem(key);
      }
    } catch (error) {
      if (!(error instanceof DOMException)) {
        Sentry.captureException(error);
      }
    }
  },
};
